<template>
  <div class="tcvelik-step-capture full-screen">
    <div v-if="!showStepsEditor" id="cameraContainer">
      <!-- Close Button - Overlaid on Camera -->
      <div class="close-button-container">
        <button @click="CloseScreen" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="camera-wrapper">
        <!-- Camera Mode -->
        <div v-if="InCameraMode" class="camera-section">
          <div class="camera-container">
            <video ref="videoElement"
                   autoplay
                   playsinline
                   muted
                   class="camera-video"></video>
            <canvas ref="canvas" style="display: none;"></canvas>

            <!-- Camera Controls Overlay -->
            <div class="camera-controls-overlay">
              <button @click="switchCamera" class="btn btn-info mx-2">
                <i class="fas fa-sync-alt"></i> {{ $t('Switch') }}
              </button>
              <button @click="capturePhoto" class="btn btn-danger mx-2">
                <i class="fas fa-camera"></i> {{ $t('Capture') }}
              </button>
              <button @click="finishCapturing" class="btn btn-success mx-2" :disabled="capturedImages.length === 0">
                <i class="fas fa-check"></i> {{ $t('Done') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Captured images counter (optional) -->
        <div v-if="capturedImages.length > 0" class="capture-counter">
          <span class="badge badge-primary">{{ $t('ImagesCaptured', { count: capturedImages.length }) }}</span>
        </div>
      </div>
    </div>

    <!-- Export Controls -->
    <div v-if="showStepsEditor" class="text-end mb-3">
      <button @click="backToCamera" class="btn btn-outline-secondary mx-2">
        <i class="fas fa-arrow-left"></i> {{ $t('Back') }}
      </button>
      <button @click="SaveSteps" class="btn btn-success mx-2">
        <i class="fas fa-save"></i> {{ $t('SaveSteps') }}
      </button>
    </div>

    <!-- Steps Editor Container -->
    <div v-if="showStepsEditor" class="steps-container">
      <div v-for="(step, index) in steps"
           :key="step.id"
           class="step-card">
        <img :src="step.image" class="step-image" :alt="$t('StepImage')" />
        <div class="mb-2">
          <vs-input v-model="step.title"
                    type="text"
                    class="form-control mb-2 w-full"
                    :placeholder="$t('StepTitle')" />
          <vs-textarea v-model="step.description"
                       class="form-control bg-white"
                       :placeholder="$t('StepDescription')"
                       rows="3"></vs-textarea>
        </div>
        <div class="controls">
          <button @click="moveStepUp(index)" class="btn btn-sm btn-secondary" :disabled="index === 0">
            <i class="fas fa-arrow-up"></i>
          </button>
          <button @click="moveStepDown(index)" class="btn btn-sm btn-secondary" :disabled="index === steps.length - 1">
            <i class="fas fa-arrow-down"></i>
          </button>
          <button @click="deleteStep(index)" class="btn btn-sm btn-danger">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import stepUtility from '@/utils/StepUtility.js'
  export default {
    name: 'TCVelikStepCapture',
    props: {
      editor: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        stepCounter: 1,
        capturedImages: [],
        steps: [],
        showStepsEditor: false,
        InCameraMode: true,
        stream: null,
        facingMode: 'environment',
      }
    },

    mounted() {
      document.body.style.overflow = "hidden"
      if (!this.checkCameraSupport()) {
        alert(this.$t('BrowserNotSupportCamera'));
        return;
      }
      this.InCameraMode = true;
      this.$nextTick(() => {
        this.StartCamera();
      });
    },

    beforeDestroy() {
      document.body.style.overflow = "initial"
      this.stopCamera();
    },

    methods: {
      // Camera support and control methods
      checkCameraSupport() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          return false;
        }
        return true;
      },
      async capturePhoto() {
        if (!this.stream) return;

        const videoElement = this.$refs.videoElement;
        const canvas = this.$refs.canvas;
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw the current video frame to the canvas
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const imageUrl = canvas.toDataURL('image/jpeg', 0.9);

        // Add to captured images
        this.capturedImages.push(imageUrl);

        // If editor prop is provided and has CreateImageStep method, call it
        if (this.editor && typeof this.editor.CreateImageStep === 'function') {
          this.editor.CreateImageStep(imageUrl);
        }
      },

      canvasToBlob(canvas) {
        return new Promise((resolve) => {
          canvas.toBlob(blob => {
            if (blob) {
              resolve(URL.createObjectURL(blob));
            } else {
              resolve(null);
            }
          }, 'image/jpeg', 0.9);
        });
      },

      switchCamera() {
        this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
        this.StartCamera();
      },

      async StartCamera() {
        try {
          if (this.stream) {
            this.stopCamera();
          }

          const constraints = {
            video: {
              facingMode: this.facingMode
            }
          };

          this.stream = await navigator.mediaDevices.getUserMedia(constraints);
          this.$refs.videoElement.srcObject = this.stream;
        } catch (error) {
          console.error('Error accessing camera:', error);

          if (error.name === 'NotAllowedError') {
            this.$emit('camera-error', {
              type: 'permission',
              message: 'Camera not allowed. Please allow access.'
            });
          } else if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
            this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
            this.StartCamera();
          } else {
            this.$emit('camera-error', {
              type: 'general',
              message: 'Error accessing camera.'
            });
          }
        }
      },

      stopCamera() {
        if (!this.stream) return;
        this.stream.getTracks().forEach(track => track.stop());
        if (this.$refs.videoElement) {
          this.$refs.videoElement.srcObject = null;
        }
        this.stream = null;
      },

      closeCameraMode() {
        this.stopCamera();
        this.InCameraMode = false;
      },

      // Wireframe simulation method
      simulateCapture() {
        const randomImage = this.sampleImages[Math.floor(Math.random() * this.sampleImages.length)];
        this.capturedImages.push(randomImage);
      },

      // Steps management methods
      finishCapturing() {
        if (this.capturedImages.length === 0) return;

        this.steps = this.capturedImages.map((image, index) => ({
          id: `step-${this.stepCounter + index}`,
          image: image,
          title: '',
          description: ''
        }));

        this.stepCounter += this.capturedImages.length;
        this.showStepsEditor = true;
        this.stopCamera();
      },

      backToCamera() {
        this.InCameraMode = true;
        this.$nextTick(() => {
          this.StartCamera();
        });
        this.showStepsEditor = false;
        this.InCameraMode = true;
      },

      moveStepUp(index) {
        if (index > 0) {
          const temp = this.steps[index];
          this.$set(this.steps, index, this.steps[index - 1]);
          this.$set(this.steps, index - 1, temp);
        }
      },

      moveStepDown(index) {
        if (index < this.steps.length - 1) {
          const temp = this.steps[index];
          this.$set(this.steps, index, this.steps[index + 1]);
          this.$set(this.steps, index + 1, temp);
        }
      },

      deleteStep(index) {
        this.steps.splice(index, 1);
      },

      SaveSteps() {
        stepUtility.AddCameraSteps(this.editor, this.steps)
        this.CloseScreen();
      },

      CloseScreen() {
        this.$emit("close-camera-step-screen")
      }
    }
  }</script>

<style scoped>

  .full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff;
    z-index: 9999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .tcvelik-step-capture {
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .close-button-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10001;
  }

  .close-btn {
    background: rgba(0, 0, 0, 0.7);
    border: none;
    outline: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

    .close-btn:hover {
      background: rgba(220, 53, 69, 0.9);
      transform: scale(1.05);
      outline: none;
    }

    .close-btn:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
    }

    .close-btn:active {
      outline: none;
      transform: scale(0.95);
    }

  .camera-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 0;
    position: relative;
  }

  .camera-container {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  .camera-video {
    width: 100vw;
    max-width: none;
    height: 100vh;
    object-fit: cover;
    display: block;
  }

  .camera-placeholder {
    width: 100vw;
    max-width: none;
    height: 100vh;
    object-fit: cover;
    box-shadow: none;
    display: block;
  }

  /* Camera Controls Overlay */
  .camera-controls-overlay {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    gap: 10px;
    background: rgba(0, 0, 0, 0.5);
    padding: 15px 20px;
    border-radius: 25px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

    .camera-controls-overlay .btn {
      background: rgba(255, 255, 255, 0.9);
      border: none;
      outline: none;
      color: #333;
      padding: 10px 15px;
      border-radius: 20px;
      font-weight: 500;
      transition: all 0.3s ease;
      backdrop-filter: blur(5px);
      -webkit-backdrop-filter: blur(5px);
    }

      .camera-controls-overlay .btn:hover {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        outline: none;
      }

      .camera-controls-overlay .btn:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
      }

    .camera-controls-overlay .btn-danger {
      background: rgba(220, 53, 69, 0.9);
      color: white;
    }

      .camera-controls-overlay .btn-danger:hover {
        background: rgba(220, 53, 69, 1);
      }

    .camera-controls-overlay .btn-info {
      background: rgba(23, 162, 184, 0.9);
      color: white;
    }

      .camera-controls-overlay .btn-info:hover {
        background: rgba(23, 162, 184, 1);
      }

    .camera-controls-overlay .btn-secondary {
      background: rgba(108, 117, 125, 0.9);
      color: white;
    }

      .camera-controls-overlay .btn-secondary:hover {
        background: rgba(108, 117, 125, 1);
      }

    .camera-controls-overlay .btn:disabled {
      background: rgba(108, 117, 125, 0.5);
      color: rgba(255, 255, 255, 0.6);
      cursor: not-allowed;
    }

      .camera-controls-overlay .btn:disabled:hover {
        transform: none;
        box-shadow: none;
      }

  .capture-counter {
    position: absolute;
    top: 10%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
  }

    .capture-counter .badge {
      background: #007bff;
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
    }

  .step-card {
    border: 1px solid #ccc;
    border-radius: 12px;
    padding: 15px;
    margin-bottom: 15px;
    background: #f9f9f9;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .step-image {
    max-width: 100%;
    border-radius: 8px;
    margin-bottom: 10px;
  }

  .controls {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .steps-container {
    max-height: 100%;
    overflow:scroll;
    scroll-behavior: smooth;
  }

  .btn {
    transition: all 0.2s ease;
  }

    .btn:hover {
      transform: translateY(-1px);
    }

  @media (max-width: 768px) {
    .close-button-container {
      top: 15px;
      right: 15px;
    }

    .close-btn {
      width: 45px;
      height: 45px;
      font-size: 16px;
    }

    .camera-controls-overlay {
      bottom: 15px;
      padding: 12px 15px;
      gap: 8px;
    }

      .camera-controls-overlay .btn {
        padding: 8px 12px;
        font-size: 14px;
      }

    .controls {
      justify-content: center;
    }

    .camera-video {
      max-width: 340px;
    }

    .camera-wrapper {
        overflow:hidden;
    }

    .tcvelik-step-capture {
      padding: 0px;
    }

    .header-container {
      max-width: 380px;
    }

    .camera-wrapper {
        width:100%;
    }

    .steps-container {
      max-width: 380px;
    }

    @media not -webkit-device-pixel-ratio {
      .camera-controls-overlay {
          bottom:50px;
      }
    }

    .camera-placeholder {
      max-width: 340px;
    }
  }
</style>
